import {
  acknowledge,
  getAlertCenter,
  markOpened,
  openDrawer,
  pruneDay,
  ringingKeys,
  setDrawerTab,
  syncWaitingFromStorage,
  type DrawerTab,
} from './alertCenterStore';

/**
 * What acknowledging or opening an alert means outside this page: the other Hub tabs of this
 * browser follow (BroadcastChannel), and the alert's system notification leaves the tray.
 */

export const ALERT_CHANNEL = 'hub-alerts';
/** Key of the start-of-day briefing's tray copy. */
export const BRIEFING_KEY = 'briefing';
/** The alert setup sound test (service worker key): never an order alarm. */
export const SETUP_TEST_KEY = 'setup-test';

type Keys = ReadonlyArray<string> | 'all';
export type ChannelMessage =
  | { type: 'seen'; keys: Keys }
  | { type: 'opened'; key: string }
  // Older Hub tabs (before the alert center) send this when an alert is acknowledged.
  | { type: 'dismiss'; keys: Keys };

let channel: BroadcastChannel | null = null;
const getChannel = () => {
  if (!channel && typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(ALERT_CHANNEL);
  }
  return channel;
};

/** Applies another tab's acknowledgement here (never re-broadcast). */
export const applyChannelMessage = (message: ChannelMessage | null | undefined) => {
  if (!message) return;
  if (message.type === 'seen') acknowledge(message.keys);
  // An older Hub tab's Acknowledge (`all`) meant its ringing alarms, not alerts nobody looked at.
  if (message.type === 'dismiss')
    acknowledge(message.keys === 'all' ? ringingKeys() : message.keys);
  if (message.type === 'opened') markOpened(message.key);
};

/** Follows the other Hub tabs: their acknowledgements, and waiting lists they load. */
export const listenToOtherTabs = () => {
  const onStorage = (event: StorageEvent) => syncWaitingFromStorage(event.key, event.newValue);
  window.addEventListener('storage', onStorage);
  // A separate instance: a BroadcastChannel never receives its own messages.
  const listener =
    typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(ALERT_CHANNEL);
  if (listener) {
    listener.onmessage = (event: MessageEvent<ChannelMessage>) => applyChannelMessage(event.data);
  }
  return () => {
    window.removeEventListener('storage', onStorage);
    listener?.close();
  };
};

const PRUNE_EVERY_MS = 60_000;

/** A Hub left open overnight drops yesterday's alerts and waiting list at the day's start. */
export const watchWorkingDay = () => {
  const prune = () => pruneDay();
  const timer = window.setInterval(prune, PRUNE_EVERY_MS);
  document.addEventListener('visibilitychange', prune);
  return () => {
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', prune);
  };
};

/** Closes the system notifications of alerts handled in the app (never the briefing or setup test). */
export const closeSystemNotifications = async (keys: Keys) => {
  try {
    const registration = await navigator.serviceWorker?.ready;
    const shown = (await registration?.getNotifications?.()) ?? [];
    shown.forEach(shownNotification => {
      const key = (shownNotification.data as { key?: string } | null)?.key;
      const matches =
        keys === 'all'
          ? shownNotification.tag?.startsWith('hub-') &&
            key !== BRIEFING_KEY &&
            key !== SETUP_TEST_KEY
          : Boolean(key && keys.includes(key));
      if (matches) shownNotification.close();
    });
  } catch {
    // Nothing to close.
  }
};

/** Alerts acknowledged here: the other tabs follow and their tray copies close. */
const seenElsewhere = (keys: ReadonlyArray<string>) => {
  if (keys.length === 0) return;
  getChannel()?.postMessage({ type: 'seen', keys } satisfies ChannelMessage);
  // Hub tabs still on the previous build (until their update) only understand `dismiss`.
  getChannel()?.postMessage({ type: 'dismiss', keys } satisfies ChannelMessage);
  void closeSystemNotifications(keys);
};

/** Acknowledge here, in the other tabs and in the tray. */
export const acknowledgeEverywhere = (keys: ReadonlyArray<string>) => {
  acknowledge(keys);
  seenElsewhere(keys);
};

/**
 * Acknowledge (alert bar, drawer): stops what rings. Quiet and background alerts not looked at
 * yet stay new on the bell.
 */
export const acknowledgeRinging = () => acknowledgeEverywhere(ringingKeys());

/** What a shown tab marked seen is seen in every tab; a shown waiting list leaves the tray. */
const shown = (tab: DrawerTab, acknowledged: ReadonlyArray<string>) => {
  seenElsewhere(acknowledged);
  if (tab === 'waiting') void closeSystemNotifications([BRIEFING_KEY]);
};

/** Opens the drawer (bell, View). */
export const viewAlerts = (tab?: DrawerTab, options?: { auto?: boolean }) => {
  const acknowledged = openDrawer(tab, options);
  shown(getAlertCenter().drawer.tab, acknowledged);
};

export const viewTab = (tab: DrawerTab) => shown(tab, setDrawerTab(tab));

export const openedEverywhere = (key: string) => {
  markOpened(key);
  getChannel()?.postMessage({ type: 'opened', key } satisfies ChannelMessage);
  getChannel()?.postMessage({ type: 'dismiss', keys: [key] } satisfies ChannelMessage);
  void closeSystemNotifications([key]);
};
